import { Alert, AlertColor, Box, Button, IconButton, Menu, MenuItem, Snackbar } from '@mui/material';
import { useState } from 'react';
import useQueueStatusData from '../../hooks/useQueueStatusData';
import { QueueModel, QueueStatusCheckModel, QueueActionParam } from '../../types/queueStatus';
import { Dictionary, groupBy, map } from 'lodash';
import { MoreHoriz, CheckCircle } from '@mui/icons-material';

import statusApiFunctions from '../../api/status';

import styles from './index.module.css';

const QueueStatuses = () => {
  const [lastRefreshTime, setLastRefreshTime] = useState<string>();
  const [queueData, setQueueData] = useState<Array<QueueStatusCheckModel | undefined>>([]);
  const [groupedQueues, setGroupedQueues] = useState<Dictionary<Array<QueueModel>>>({});
  const [apiNames, setApiNames] = useState<Array<string> | undefined>();
  const [menuContext, setMenuContext] = useState<React.JSX.Element[] | undefined>();
  const [resStatus, setResStatus] = useState<AlertColor>('success');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: any, queueModel: QueueModel) => {
    setAnchorEl(event.currentTarget);
    createMenuContext(queueModel);
  };

  const handleAction = async (queueModel: QueueModel, actionName: string) => {
    setAnchorEl(null);
    let actionParam: QueueActionParam = {
      queueModel: queueModel,
      queueActionName: actionName,
    };
    const res = await statusApiFunctions.performQueueAction(actionParam);
    setResStatus(res === '' ? 'success' : 'error');
    setOpenSnackbar(true);
    refetch();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const createMenuContext = (queueModel: QueueModel) => {
    setMenuContext(
      queueData
        ?.find(
          (x) =>
            x?.queueModel.queueName === queueModel.queueName && x?.queueModel.subQueueName === queueModel.subQueueName,
        )
        ?.availableQueueActionNames.map((a) => {
          return (
            <MenuItem
              data-testid={a}
              key={a}
              onClick={() => {
                handleAction(queueModel, a);
              }}>
              {a}
            </MenuItem>
          );
        }),
    );
  };

  const handleSuccess = (data: any) => {
    if (data) {
      setQueueData(data);
      const queues = map(data, (x) => {
        return x.queueModel;
      });
      const grouped = groupBy(queues, 'apiName');
      setGroupedQueues(grouped);
      setApiNames(Object.keys(grouped));
      setLastRefreshTime(new Date().toLocaleString());
    }
  };

  const handleSnackbarClose = (event: any, reason: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const { isLoading, isError, refetch } = useQueueStatusData(handleSuccess);

  return (
    <Box className={styles.queueStatusCheckContainer}>
      <Box color="text.secondary" className={styles.panelLabel}>
        Queue Status Checks
      </Box>
      {!isLoading && !isError && apiNames ? (
        apiNames?.map((x, ci) => {
          return (
            <Box className={styles.queueApiContainer} key={ci}>
              <Box className={styles.apiHeader} color="text.secondary">
                {x}
              </Box>
              <Box className={styles.queueContainer} key={`${ci}-sub`}>
                {groupedQueues![x].map((q, qi) => {
                  let queueRow = queueData?.find
                    ? queueData?.find(
                        (x) => x?.queueModel.queueName === q.queueName && x?.queueModel.subQueueName === q.subQueueName,
                      )
                    : null;
                  return (
                    <Box key={qi}>
                      <Box className={styles.queueRow}>
                        <Box className={styles.queueName}>{q.queueName}</Box>
                        <Box className={styles.subQueueName}>{q.subQueueName}</Box>{' '}
                        <Box className={styles.queueCount}>{queueRow?.count}</Box>
                        {queueRow && queueRow?.availableQueueActionNames!.length > 0 && (
                          <Box className={styles.queueAction}>
                            <IconButton
                              aria-label="actions"
                              id={'contextButton' + qi}
                              aria-controls={open ? 'menu' : undefined}
                              aria-expanded={open ? 'true' : undefined}
                              aria-haspopup="true"
                              onClick={(e: any) => {
                                handleClick(e, q);
                              }}>
                              <MoreHoriz fontSize="small" />
                            </IconButton>{' '}
                            <Menu
                              id={q.queueName + q.subQueueName}
                              anchorEl={anchorEl}
                              open={open}
                              onClose={handleClose}
                              MenuListProps={{
                                'aria-labelledby': 'basic-button',
                              }}>
                              {menuContext}
                            </Menu>{' '}
                          </Box>
                        )}
                      </Box>
                      <hr style={{ width: '100%', color: 'e0e0e0', opacity: '0.5' }}></hr>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })
      ) : (
        <Box>
          {!isLoading && !apiNames
            ? 'An error has occurred while trying to load the available Queue Statuses'
            : 'Loading...'}
        </Box>
      )}
      <Box className={styles.refreshContainer}>
        <Box className={styles.refreshText}>Last refreshed: {lastRefreshTime}</Box>
        <Button
          data-testid="refresh-button"
          className={styles.refreshButton}
          onClick={() => refetch()}
          aria-label="RefreshQueueCheckButton">
          Refresh All
        </Button>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        onClose={handleSnackbarClose}>
        <Alert
          variant="outlined"
          severity={resStatus!}
          sx={{ width: '100%' }}
          iconMapping={{
            success: <CheckCircle fontSize="inherit" />,
          }}>
          {resStatus === 'success' ? 'Queue Action Succeeded' : 'There was a problem executing the Queue Action'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QueueStatuses;
